import type * as C from "@zooi/contracts";
import { addDays, localDate, weekday, zonedTime } from "@zooi/domain";

// What the sidebar and the shell decide (docs/design-system.md §0.2, plan M7
// step 7c), kept apart from the components so the rules are tested.

/** When the next weekly run is asked for: the chosen weekday and hour in the product's time zone, after now and not before a pause ends. */
export function nextWeeklyRun(settings: Pick<C.RunSettings, "weekday" | "hour" | "time_zone" | "paused_until">, now: number): string {
  const today = localDate(now, settings.time_zone);
  let date = addDays(today, (settings.weekday - weekday(today) + 7) % 7);
  if (zonedTime(date, settings.hour, 0, settings.time_zone) <= now) date = addDays(date, 7);
  // A pause holds requests until the start of its day (RunSettings.paused_until).
  while (settings.paused_until && date < settings.paused_until) date = addDays(date, 7);
  return new Date(zonedTime(date, settings.hour, 0, settings.time_zone)).toISOString();
}

/** A product's pages in the sidebar, in order, with their G-jump keys. */
export const PRODUCT_PAGES = [
  { key: "week", jump: "w" },
  { key: "campaigns", jump: "c" },
  { key: "actions", jump: "a" },
  { key: "data", jump: "d" },
  { key: "profile", jump: "p" },
] as const;
export type ProductPageKey = (typeof PRODUCT_PAGES)[number]["key"];

/** The product a page belongs to, from its address (`/p/:product/…`). */
export const productOfPath = (pathname: string): string | null => /^\/p\/([^/?#]+)/.exec(pathname)?.[1] ?? null;

/** Which of the product's pages the address is on, if any. */
export function productPageOfPath(pathname: string): ProductPageKey | null {
  const rest = /^\/p\/[^/?#]+\/([^/?#]+)/.exec(pathname)?.[1];
  return PRODUCT_PAGES.find((page) => page.key === rest)?.key ?? null;
}

/** Per product: how many inbox items wait, and whether a campaign reading is due. */
export function productBadges(items: readonly C.InboxItem[]): Map<string, { count: number; readingDue: boolean }> {
  const out = new Map<string, { count: number; readingDue: boolean }>();
  for (const item of items) {
    const entry = out.get(item.product.id) ?? { count: 0, readingDue: false };
    entry.count += 1;
    if (item.kind === "campaign_reading") entry.readingDue = true;
    out.set(item.product.id, entry);
  }
  return out;
}

/** Which product groups are open: the person's choice on this device, else only the current product. */
export function readOpenProducts(raw: string | null): Record<string, boolean> {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).filter((entry): entry is [string, boolean] => typeof entry[1] === "boolean"));
  } catch {
    return {};
  }
}

export const isProductOpen = (open: Record<string, boolean>, id: string, current: string | null | undefined): boolean => open[id] ?? id === current;
